import { useState, useMemo } from 'react';
import { HedgeAnalysis, estimateVolatility, estimateOptionPremium } from '@/lib/hedgeAnalysis';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Shield, TrendingDown, ArrowUpDown, DollarSign, GitCompareArrows, Zap, AlertTriangle } from 'lucide-react';
import { StrategyPnLChart } from './StrategyPnLChart';
import { StrategyCompareChart } from './StrategyCompareChart';
import { cn } from '@/lib/utils';

interface Props {
  analyses: HedgeAnalysis[];
  totalPortfolioValue: number;
}

function riskColor(score: number): string {
  if (score <= 3) return 'bg-green-500/20 text-green-700 dark:text-green-400';
  if (score <= 6) return 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400';
  return 'bg-red-500/20 text-red-700 dark:text-red-400';
}

function formatCurrency(val: number, currency: string = 'USD'): string {
  const sym = currency === 'EUR' ? '€' : currency === 'CHF' ? 'CHF ' : '$';
  return `${sym}${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function HedgeResultsPanel({ analyses, totalPortfolioValue }: Props) {
  const [activePnL, setActivePnL] = useState<Record<string, { strategy: 'put' | 'collar' | 'covered'; putIndex?: number } | null>>({});
  const [compareOpen, setCompareOpen] = useState<Record<string, boolean>>({});
  const totalHedgeCostATM = analyses.reduce((s, a) => s + a.protectivePuts[0].totalCost, 0);
  const totalHedgeCost5OTM = analyses.reduce((s, a) => s + a.protectivePuts[1].totalCost, 0);
  const highRiskPositions = analyses.filter(a => a.riskScore >= 7);

  // Compute gamma for each position (ATM, 90d default)
  const gammaAlerts = useMemo(() => {
    const GAMMA_THRESHOLD = 0.05;
    return analyses.map(a => {
      const pos = a.position;
      const spot = pos.currentPrice;
      const iv = estimateVolatility(pos.gainPct);
      const strike = spot; // ATM
      const h = 0.5;
      const computeGamma = (dte: number) => {
        const pUp = estimateOptionPremium(spot + h, strike, iv, Math.max(dte, 0.5), 'put');
        const pMid = estimateOptionPremium(spot, strike, iv, Math.max(dte, 0.5), 'put');
        const pDown = estimateOptionPremium(spot - h, strike, iv, Math.max(dte, 0.5), 'put');
        return Math.abs((pUp - 2 * pMid + pDown) / (h * h));
      };
      const gamma90 = computeGamma(90);
      const gamma30 = computeGamma(30);
      const gamma7 = computeGamma(7);
      return {
        ticker: pos.ticker,
        gamma90,
        gamma30,
        gamma7,
        iv,
        isHighGamma: gamma30 > GAMMA_THRESHOLD || gamma7 > GAMMA_THRESHOLD,
        alertLevel: gamma7 > 0.1 ? 'critical' : gamma7 > GAMMA_THRESHOLD ? 'warning' : gamma30 > GAMMA_THRESHOLD ? 'caution' : 'ok',
      };
    });
  }, [analyses]);

  const highGammaPositions = gammaAlerts.filter(g => g.isHighGamma);

  const togglePnL = (ticker: string, strategy: 'put' | 'collar' | 'covered', putIndex?: number) => {
    setActivePnL(prev => {
      const current = prev[ticker];
      if (current && current.strategy === strategy && current.putIndex === putIndex) {
        return { ...prev, [ticker]: null }; // toggle off
      }
      return { ...prev, [ticker]: { strategy, putIndex } };
    });
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-sm text-muted-foreground">Portfolio Value</p>
            <p className="text-2xl font-bold text-foreground">{formatCurrency(totalPortfolioValue)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-sm text-muted-foreground">ATM Put Hedge Cost</p>
            <p className="text-2xl font-bold text-destructive">{formatCurrency(totalHedgeCostATM)}</p>
            <p className="text-xs text-muted-foreground">{((totalHedgeCostATM / totalPortfolioValue) * 100).toFixed(2)}% of portfolio</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-sm text-muted-foreground">5% OTM Hedge Cost</p>
            <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{formatCurrency(totalHedgeCost5OTM)}</p>
            <p className="text-xs text-muted-foreground">{((totalHedgeCost5OTM / totalPortfolioValue) * 100).toFixed(2)}% of portfolio</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-sm text-muted-foreground">High Risk Positions</p>
            <p className="text-2xl font-bold text-destructive">{highRiskPositions.length}</p>
            <p className="text-xs text-muted-foreground">of {analyses.length} total</p>
          </CardContent>
        </Card>
      </div>

      {/* Greeks Risk Alert */}
      {highGammaPositions.length > 0 && (
        <Card className="border-2 border-destructive/40 bg-destructive/5">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-full bg-destructive/15 mt-0.5">
                <Zap className="h-5 w-5 text-destructive" />
              </div>
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-bold text-destructive">⚡ Gamma Risk Alert — {highGammaPositions.length} Position{highGammaPositions.length > 1 ? 's' : ''}</h3>
                  <Badge variant="destructive" className="text-[10px]">HIGH GAMMA</Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  These positions have gamma above 0.05 — small price moves will cause large delta swings. Consider rolling hedges early or adjusting strike prices.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 mt-2">
                  {highGammaPositions.map(g => (
                    <div
                      key={g.ticker}
                      className={cn(
                        "p-2.5 rounded-lg border text-xs space-y-1",
                        g.alertLevel === 'critical' ? 'border-destructive/50 bg-destructive/10' :
                        g.alertLevel === 'warning' ? 'border-orange-500/40 bg-orange-500/10' :
                        'border-yellow-500/30 bg-yellow-500/10'
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-foreground">{g.ticker}</span>
                        <Badge
                          variant="outline"
                          className={cn("text-[9px] px-1.5",
                            g.alertLevel === 'critical' ? 'border-destructive text-destructive' :
                            g.alertLevel === 'warning' ? 'border-orange-500 text-orange-500' :
                            'border-yellow-500 text-yellow-500'
                          )}
                        >
                          {g.alertLevel === 'critical' ? '🔴 CRITICAL' : g.alertLevel === 'warning' ? '🟠 WARNING' : '🟡 CAUTION'}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-1 text-[10px]">
                        <div className="text-center">
                          <p className="text-muted-foreground">γ 90d</p>
                          <p className="font-mono font-bold text-foreground">{g.gamma90.toFixed(4)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-muted-foreground">γ 30d</p>
                          <p className={cn("font-mono font-bold", g.gamma30 > 0.05 ? 'text-orange-500' : 'text-foreground')}>{g.gamma30.toFixed(4)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-muted-foreground">γ 7d</p>
                          <p className={cn("font-mono font-bold", g.gamma7 > 0.1 ? 'text-destructive' : g.gamma7 > 0.05 ? 'text-orange-500' : 'text-foreground')}>{g.gamma7.toFixed(4)}</p>
                        </div>
                      </div>
                      <p className="text-[10px] text-muted-foreground">IV: {(g.iv * 100).toFixed(0)}%</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Per-position analysis */}
      <div className="space-y-4">
        {analyses
          .sort((a, b) => b.riskScore - a.riskScore)
          .map((analysis) => {
          const gammaInfo = gammaAlerts.find(g => g.ticker === analysis.position.ticker);
          return (
          <Card key={analysis.position.ticker} className="shadow-md">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CardTitle className="text-lg">{analysis.position.ticker}</CardTitle>
                  <span className="text-sm text-muted-foreground">{analysis.position.stockName}</span>
                </div>
                <div className="flex items-center gap-2">
                  {gammaInfo && gammaInfo.isHighGamma && (
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge
                            variant="outline"
                            className={cn("text-[10px] gap-1 cursor-help",
                              gammaInfo.alertLevel === 'critical' ? 'border-destructive/50 bg-destructive/10 text-destructive' :
                              gammaInfo.alertLevel === 'warning' ? 'border-orange-500/50 bg-orange-500/10 text-orange-500' :
                              'border-yellow-500/50 bg-yellow-500/10 text-yellow-500'
                            )}
                          >
                            <Zap className="h-3 w-3" />
                            γ {gammaInfo.gamma7.toFixed(3)}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="p-3 space-y-2 min-w-[180px]">
                          <p className="text-xs font-bold text-foreground flex items-center gap-1.5">
                            <Zap className="h-3.5 w-3.5" /> Gamma Breakdown — {analysis.position.ticker}
                          </p>
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div>
                              <p className="text-[10px] text-muted-foreground">90 DTE</p>
                              <p className="text-xs font-mono font-bold text-foreground">{gammaInfo.gamma90.toFixed(4)}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-muted-foreground">30 DTE</p>
                              <p className={cn("text-xs font-mono font-bold", gammaInfo.gamma30 > 0.05 ? 'text-orange-500' : 'text-foreground')}>{gammaInfo.gamma30.toFixed(4)}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-muted-foreground">7 DTE</p>
                              <p className={cn("text-xs font-mono font-bold", gammaInfo.gamma7 > 0.1 ? 'text-destructive' : gammaInfo.gamma7 > 0.05 ? 'text-orange-500' : 'text-foreground')}>{gammaInfo.gamma7.toFixed(4)}</p>
                            </div>
                          </div>
                          <p className="text-[10px] text-muted-foreground">IV: {(gammaInfo.iv * 100).toFixed(0)}% | ATM strike</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  <Badge variant="outline" className={riskColor(analysis.riskScore)}>
                    Risk: {analysis.riskScore}/10
                  </Badge>
                  <Badge variant="outline">
                    Weight: {analysis.portfolioWeight}%
                  </Badge>
                </div>
              </div>
              <CardDescription>
                {analysis.position.quantity} shares @ {formatCurrency(analysis.position.currentPrice, analysis.position.localCurrency)} | 
                Total: {formatCurrency(analysis.position.totalValue, analysis.position.localCurrency)} | 
                Gain: {(analysis.position.gainPct * 100).toFixed(1)}%
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Compare button */}
              <div className="flex justify-end mb-2">
                <Button
                  variant={compareOpen[analysis.position.ticker] ? "default" : "outline"}
                  size="sm"
                  className="gap-1.5 text-xs"
                  onClick={() => setCompareOpen(prev => ({ ...prev, [analysis.position.ticker]: !prev[analysis.position.ticker] }))}
                >
                  <GitCompareArrows className="h-3.5 w-3.5" />
                  {compareOpen[analysis.position.ticker] ? 'Hide Comparison' : 'Compare Strategies'}
                </Button>
              </div>

              {compareOpen[analysis.position.ticker] && (
                <StrategyCompareChart
                  analysis={analysis}
                  onClose={() => setCompareOpen(prev => ({ ...prev, [analysis.position.ticker]: false }))}
                />
              )}

              <Tabs defaultValue="puts" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="puts" className="gap-1">
                    <Shield className="h-3 w-3" />
                    Protective Puts
                  </TabsTrigger>
                  <TabsTrigger value="collar" className="gap-1">
                    <ArrowUpDown className="h-3 w-3" />
                    Collar
                  </TabsTrigger>
                  <TabsTrigger value="covered" className="gap-1">
                    <DollarSign className="h-3 w-3" />
                    Covered Call
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="puts" className="mt-3">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-2 px-3 font-medium text-muted-foreground">Protection</th>
                          <th className="text-right py-2 px-3 font-medium text-muted-foreground">Strike</th>
                          <th className="text-right py-2 px-3 font-medium text-muted-foreground">Premium/sh</th>
                          <th className="text-right py-2 px-3 font-medium text-muted-foreground">Contracts</th>
                          <th className="text-right py-2 px-3 font-medium text-muted-foreground">Total Cost</th>
                          <th className="text-right py-2 px-3 font-medium text-muted-foreground">Max Loss</th>
                          <th className="text-right py-2 px-3 font-medium text-muted-foreground">Break-Even</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analysis.protectivePuts.map((put, i) => {
                          const isSelected = activePnL[analysis.position.ticker]?.strategy === 'put' && activePnL[analysis.position.ticker]?.putIndex === i;
                          return (
                            <tr
                              key={i}
                              className={cn(
                                "border-b border-border/30 cursor-pointer transition-colors",
                                isSelected ? "bg-primary/10 ring-1 ring-primary/30" : "hover:bg-muted/30"
                              )}
                              onClick={() => togglePnL(analysis.position.ticker, 'put', i)}
                              title="Click to see P&L simulation"
                            >
                              <td className="py-2 px-3">
                                <Badge variant={isSelected ? "default" : "secondary"}>{put.protectionLevel}</Badge>
                              </td>
                              <td className="py-2 px-3 text-right font-mono">{formatCurrency(put.strikePrice)}</td>
                              <td className="py-2 px-3 text-right font-mono">{formatCurrency(put.estimatedPremium)}</td>
                              <td className="py-2 px-3 text-right">{put.contractsNeeded}</td>
                              <td className="py-2 px-3 text-right font-mono text-destructive">{formatCurrency(put.totalCost)}</td>
                              <td className="py-2 px-3 text-right font-mono">{formatCurrency(put.maxLoss)}</td>
                              <td className="py-2 px-3 text-right font-mono">{formatCurrency(put.breakEvenPrice)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 text-center">👆 Click any row above to open P&L simulator</p>
                  {activePnL[analysis.position.ticker]?.strategy === 'put' && (
                    <StrategyPnLChart analysis={analysis} strategy="put" putIndex={activePnL[analysis.position.ticker]?.putIndex ?? 1} />
                  )}
                </TabsContent>

                <TabsContent value="collar" className="mt-3">
                  <div className="cursor-pointer" onClick={() => togglePnL(analysis.position.ticker, 'collar')}>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-3 bg-muted/30 rounded-lg">
                        <p className="text-xs text-muted-foreground">Put Strike (Floor)</p>
                        <p className="text-lg font-bold text-foreground">{formatCurrency(analysis.collar.putStrike)}</p>
                        <p className="text-xs text-muted-foreground">({analysis.collar.protectionFloor}% of spot)</p>
                      </div>
                      <div className="p-3 bg-muted/30 rounded-lg">
                        <p className="text-xs text-muted-foreground">Call Strike (Cap)</p>
                        <p className="text-lg font-bold text-foreground">{formatCurrency(analysis.collar.callStrike)}</p>
                        <p className="text-xs text-muted-foreground">({analysis.collar.gainCap}% of spot)</p>
                      </div>
                      <div className="p-3 bg-muted/30 rounded-lg">
                        <p className="text-xs text-muted-foreground">Net Premium</p>
                        <p className={`text-lg font-bold ${analysis.collar.netPremium > 0 ? 'text-destructive' : 'text-green-600 dark:text-green-400'}`}>
                          {analysis.collar.netPremium > 0 ? 'Debit' : 'Credit'}: {formatCurrency(Math.abs(analysis.collar.netPremium))}
                        </p>
                      </div>
                      <div className="p-3 bg-muted/30 rounded-lg">
                        <p className="text-xs text-muted-foreground">Contracts</p>
                        <p className="text-lg font-bold text-foreground">{analysis.collar.contractsNeeded}</p>
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-4">
                      <div className="p-3 border border-destructive/20 rounded-lg">
                        <p className="text-xs text-muted-foreground">Max Loss</p>
                        <p className="text-lg font-bold text-destructive">{formatCurrency(analysis.collar.maxLoss)}</p>
                      </div>
                      <div className="p-3 border border-green-500/20 rounded-lg">
                        <p className="text-xs text-muted-foreground">Max Gain</p>
                        <p className="text-lg font-bold text-green-600 dark:text-green-400">{formatCurrency(analysis.collar.maxGain)}</p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2 text-center">👆 Click to {activePnL[analysis.position.ticker]?.strategy === 'collar' ? 'hide' : 'open'} P&L simulator</p>
                  </div>
                  {activePnL[analysis.position.ticker]?.strategy === 'collar' && (
                    <StrategyPnLChart analysis={analysis} strategy="collar" />
                  )}
                </TabsContent>

                <TabsContent value="covered" className="mt-3">
                  {analysis.coveredCall.contractsNeeded === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">
                      Requires at least 100 shares. Current: {analysis.position.quantity} shares.
                    </p>
                  ) : (
                    <div className="cursor-pointer" onClick={() => togglePnL(analysis.position.ticker, 'covered')}>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div className="p-3 bg-muted/30 rounded-lg">
                          <p className="text-xs text-muted-foreground">Strike Price</p>
                          <p className="text-lg font-bold text-foreground">{formatCurrency(analysis.coveredCall.strikePrice)}</p>
                        </div>
                        <div className="p-3 bg-muted/30 rounded-lg">
                          <p className="text-xs text-muted-foreground">Premium/Share</p>
                          <p className="text-lg font-bold text-foreground">{formatCurrency(analysis.coveredCall.estimatedPremium)}</p>
                        </div>
                        <div className="p-3 bg-green-500/10 rounded-lg">
                          <p className="text-xs text-muted-foreground">Income Generated</p>
                          <p className="text-lg font-bold text-green-600 dark:text-green-400">{formatCurrency(analysis.coveredCall.incomeGenerated)}</p>
                        </div>
                        <div className="p-3 bg-muted/30 rounded-lg">
                          <p className="text-xs text-muted-foreground">Contracts</p>
                          <p className="text-lg font-bold text-foreground">{analysis.coveredCall.contractsNeeded}</p>
                        </div>
                        <div className="p-3 bg-muted/30 rounded-lg">
                          <p className="text-xs text-muted-foreground">Downside Buffer</p>
                          <p className="text-lg font-bold text-foreground">{analysis.coveredCall.downsideBuffer}%</p>
                        </div>
                        <div className="p-3 bg-muted/30 rounded-lg">
                          <p className="text-xs text-muted-foreground">Max Gain</p>
                          <p className="text-lg font-bold text-green-600 dark:text-green-400">{formatCurrency(analysis.coveredCall.maxGain)}</p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2 text-center">👆 Click to {activePnL[analysis.position.ticker]?.strategy === 'covered' ? 'hide' : 'open'} P&L simulator</p>
                    </div>
                  )}
                  {activePnL[analysis.position.ticker]?.strategy === 'covered' && analysis.coveredCall.contractsNeeded > 0 && (
                    <StrategyPnLChart analysis={analysis} strategy="covered" />
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        )})}
      </div>
    </div>
  );
}
